import { type Activity, type Content, type Follows, FragmentRoutes, type News } from '~api';
import { api } from '~portal/api';
import { ACCOUNT, mergeWorkspaceMembers, WORKSPACE_MEMBERS } from './account';
import { useFollowSet } from './company';
import { operatorFixtures, useFixtureModule } from './fixtures';
import { useFragmentData } from './fragment-data';
import { liveQuery } from './live';
import { NEWS } from './news';
import { mergeOperatorGroups } from './operator-directory';
import { useOperatorDirectoryQuery } from './operator-directory-query';
import { type RegionPages, useRegionPages } from './region-pages';
import type {
  AnalystNote,
  ExploreCard,
  HomeData,
  LiveQuery,
  OperatorGroup,
  PlatformUpdate,
  SubjectRef,
  TrackedPage,
  WorkspaceMember,
} from './types';

/**
 * ILLUSTRATIVE FIXTURES — the home page's content until analyst notes, updates and follows come
 * from the CMS / tRPC. Counts (tracked operators, markets covered) are counted from the other
 * fixtures rather than typed in, so they can never claim more than the portal actually has
 * (the client caught a hard-coded "423" that matched nothing).
 *
 * UI round 2 (Lane F): Home now reads news, follows, the analyst note and the announcement live
 * (hooks at the foot of this file). What remains fixture — the recent-updates events while nothing
 * writes `activity`, the explore cards and the team — is badged on the page. The KPI strip and the
 * monthly chart left Home (gap H3, decision D-2), and their fixtures with them.
 */

const latestNote: AnalystNote = {
  id: 'note-ladbrokes-2026-10',
  title: 'Ladbrokes: UK online is turning, but the retail estate is still a drag',
  author: { name: 'Hannah Clarke', role: 'Senior Analyst, Operators' },
  date: '2026-10-10',
  excerpt:
    'Entain’s flagship UK brand grew online share for a third straight quarter in our traffic tracking. We think the improvement is real, but shop closures will keep group UK revenue flat into 2027 and the duty rise takes most of the operating leverage.',
  subject: { kind: 'operator', slug: 'entain', name: 'Entain' },
};

const recentUpdates: PlatformUpdate[] = [
  {
    id: 'update-1',
    kind: 'analyst-note',
    title: 'Analyst note added: Ladbrokes',
    date: '2026-10-10',
    subject: { kind: 'operator', slug: 'entain', name: 'Entain' },
  },
  {
    id: 'update-2',
    kind: 'financial-model',
    title: 'Financial model updated: Entain Q3',
    date: '2026-10-07',
    subject: { kind: 'operator', slug: 'entain', name: 'Entain' },
  },
  {
    id: 'update-3',
    kind: 'regulatory',
    title: 'Regulatory update: Brazil',
    date: '2026-10-03',
    subject: { kind: 'region', slug: 'brazil', name: 'Brazil' },
  },
  {
    id: 'update-4',
    kind: 'market-share',
    title: 'Market share refreshed: Spain',
    date: '2026-09-29',
    subject: { kind: 'region', slug: 'spain', name: 'Spain' },
  },
];

const tracked: TrackedPage[] = [
  {
    kind: 'operator',
    slug: 'flutter-entertainment',
    name: 'Flutter Entertainment',
    followedAt: '2026-08-14',
    lastUpdated: '2026-10-08',
  },
  {
    kind: 'operator',
    slug: 'entain',
    name: 'Entain',
    followedAt: '2026-08-14',
    lastUpdated: '2026-10-07',
  },
  {
    kind: 'region',
    slug: 'spain',
    name: 'Spain',
    followedAt: '2026-09-02',
    lastUpdated: '2026-09-29',
  },
];

/** @desc Figma's four ways in (`426:7806`, H9): titles and CTAs as the frame words them. */
const explore: ExploreCard[] = [
  {
    key: 'agent',
    title: 'H2GC Agent',
    description: 'Ask questions of H2’s data in plain English and get charts and tables back.',
  },
  {
    key: 'operators',
    title: 'Operator view',
    description: 'Company pages for the major groups: financials, market share and news.',
  },
  {
    key: 'regions',
    title: 'Region view',
    description: 'Market pages with GGR, regulation, forecasts and who leads.',
  },
  {
    key: 'reports',
    title: 'Reports',
    description: 'Build an operator, region or custom data report.',
  },
];

export const HOME: HomeData = {
  latestNote,
  recentUpdates,
  news: NEWS.slice(0, 4),
  tracked,
  explore,
  workspace: [...WORKSPACE_MEMBERS],
};

// ---------------------------------------------------------------------------------------------
// UI round 2 — the live Home (Figma `426:7806`, decision D-2)
// ---------------------------------------------------------------------------------------------

/** @desc The role line under the welcome (H1). WorkOS carries no workspace role; `isAdmin` is the portal's own check. */
export const roleLine = (admin: boolean): string =>
  admin ? 'You are the administrator for this workspace.' : 'You are a member of this workspace.';

/** @desc The signed-in user's first name, or "there" while the profile is unknown or nameless. */
export const firstNameOf = (profile: { name?: { first?: string } } | null | undefined): string =>
  profile?.name?.first?.trim() || 'there';

export type Workspace = {
  /** @desc The WorkOS organisation's name, else the fixture company's (then `nameIsIllustrative`). */
  name: string;
  nameIsIllustrative: boolean;
  members: readonly WorkspaceMember[];
};

/**
 * @desc The workspace (H1, H10): its name from the signed-in user's WorkOS organisation, and the
 * team with the signed-in user at its head (`mergeWorkspaceMembers`). The rest of the team is the
 * illustrative fixture and the page badges it so.
 */
export const useWorkspace = (): Workspace => {
  const me = api.users.me.useQuery().data;
  return {
    name: me?.organisationName || ACCOUNT.company.name,
    nameIsIllustrative: !me?.organisationName,
    members: mergeWorkspaceMembers(WORKSPACE_MEMBERS, me, ACCOUNT.profile),
  };
};

/** @desc The platform announcement banner (H2): the latest `platform` story, or `null` for no banner. */
export const useAnnouncement = (): LiveQuery<News.Announcement> =>
  liveQuery(api.news.latestPlatformAnnouncement.useQuery());

/** @desc How long the latest note stays fresh in the query cache (insights caches it too). */
const ANALYST_NOTE_STALE_MS = 60_000;

/**
 * @desc The highlighted first row of Recent updates (D-2): the latest approved note, or `null`.
 * Read from the insights data fragment `/fragments/d/content/analyst-note/latest` (round 3 lane 6)
 * and drawn by the SPA as the same row as before.
 */
export const useLatestAnalystNote = (): LiveQuery<Content.AnalystNote | null> =>
  useFragmentData({
    path: FragmentRoutes.paths.data.analystNoteLatest,
    // The decoder (and the Zod schema it needs) loads with the first answer, not the entry chunk.
    decode: async (body) => (await import('./fragment-decoders')).decodeLatestAnalystNote(body),
    staleTime: ANALYST_NOTE_STALE_MS,
  });

/**
 * @desc Activity for Recent updates (H4): platform events and the caller's own follows
 * (`docs/activity-events.md`). While the feed is still empty the page shows the badged fixture
 * events instead; an error is an error and is shown as one.
 */
export const useRecentActivity = (limit = 4): LiveQuery<readonly Activity.Event[]> =>
  liveQuery(api.activity.recent.useQuery({ limit }));

/**
 * @desc The story kinds Home's Recent news and the `/news` listing show: everything but platform
 * announcements, which are the Home banner (H2), not news.
 */
export const NEWS_FEED_KINDS: News.Kind[] = ['regulatory', 'company', 'market'];

/**
 * @desc The four latest stories (H5), from the live feed, announcements excluded. The server
 * collapses a headline repeated on one day (H2-310), so the four are four different stories.
 */
export const useRecentNews = (limit = 4): LiveQuery<readonly News.Item[]> => {
  const query = api.news.list.useQuery({ page: 1, limit, kinds: NEWS_FEED_KINDS });
  return {
    data: query.data?.items,
    isLoading: query.isPending,
    error: query.error,
  };
};

/** @desc A followed page joined to what its listing knows about it (H6). */
export type TrackedCard = {
  type: Follows.Follow['entityType'];
  slug: string;
  name: string;
  /** @desc The Figma eyebrow: an operator's classification, a region's continent (F-3: consistent). */
  eyebrow: string;
  followedAt: Date;
  /** @desc Operators: where the group operates, one line. */
  description?: string;
  logoSlug?: string;
  /** @desc Operators: the company's Sanity logo URL, unsized. */
  logoUrl?: string;
  flagCode?: string;
  /** @desc The tile letters, for a region the warehouse has no ISO code for. */
  code?: string;
  /** @desc The follow names a page neither listing knows — shown by slug rather than hidden. */
  unresolved?: boolean;
};

const ENTITY_TYPE_EYEBROW: Record<OperatorGroup['classification'], string> = {
  'operator-b2c': 'Operator',
  'supplier-b2b': 'Supplier',
  'national-operator': 'National operator',
};

/** @desc `flutter-entertainment` → `Flutter entertainment`, for a follow nothing can name. */
const humanise = (slug: string) =>
  slug.replace(/-/g, ' ').replace(/^\w/, (first) => first.toUpperCase());

/**
 * @desc Joins the caller's follows with the operators fixture and the region pages (the contract
 * carries only `entityType`, `slug` and `followedAt`; names and tiles belong to the pages). A region
 * is any page the region route renders — a live market, a fixture market or state, the roll-up or
 * a continent (`resolveRegionPages`) — named as that page names itself. Order is the follows' own —
 * most recent first. A follow no page exists for is kept and marked, so the section never shows
 * the empty state while something is followed.
 */
export const joinTrackedPages = (
  follows: readonly Follows.Follow[],
  groups: readonly OperatorGroup[],
  regions: RegionPages,
): TrackedCard[] =>
  follows.map((follow): TrackedCard => {
    if (follow.entityType === 'operator') {
      const group = groups.find((candidate) => candidate.slug === follow.slug);
      if (!group) {
        return {
          type: 'operator',
          slug: follow.slug,
          name: humanise(follow.slug),
          eyebrow: 'Operator',
          followedAt: follow.followedAt,
          unresolved: true,
        };
      }
      return {
        type: 'operator',
        slug: group.slug,
        name: group.name,
        eyebrow: ENTITY_TYPE_EYEBROW[group.classification],
        followedAt: follow.followedAt,
        description: group.footprint,
        logoSlug: group.slug,
        ...(group.logoUrl ? { logoUrl: group.logoUrl } : {}),
      };
    }
    const page = regions.get(follow.slug);
    if (!page) {
      return {
        type: 'region',
        slug: follow.slug,
        name: humanise(follow.slug),
        eyebrow: 'Region',
        followedAt: follow.followedAt,
        unresolved: true,
      };
    }
    return {
      type: 'region',
      slug: page.slug,
      name: page.name,
      eyebrow: page.eyebrow,
      followedAt: follow.followedAt,
      ...(page.flagCode ? { flagCode: page.flagCode } : {}),
      ...(page.code ? { code: page.code } : {}),
    };
  });

/**
 * @desc The user's tracked pages (H6): the caller's follows (`useFollowSet`, the same cache every
 * bookmark reads) joined with the operators fixture (lazily loaded) and the region pages
 * (`useRegionPages`: the shared `markets.list` directory plus the lazily loaded regions fixture —
 * the two sources the region route itself renders from). The join waits for both while a region is
 * followed, so a region card is never drawn by its slug while its name is still loading.
 */
export const useTrackedPages = (): LiveQuery<readonly TrackedCard[]> => {
  const follows = useFollowSet();
  const needsRegions = (follows.data ?? []).some((follow) => follow.entityType === 'region');
  const needsGroups = (follows.data ?? []).some((follow) => follow.entityType === 'operator');
  const regions = useRegionPages(needsRegions);
  // The operators fixture is a lazy chunk (`fixtures.ts`), requested only once an operator is
  // followed — so the home page's first paint never waits on it. The company directory
  // (`content.operatorGroups`) is likewise only read then; the two merge as on the operator pages.
  const operators = useFixtureModule(operatorFixtures, needsGroups);
  const directory = useOperatorDirectoryQuery(needsGroups);
  const regionsReady = !needsRegions || regions.data !== undefined;
  const groupsReady =
    !needsGroups || (operators.module !== undefined && directory.data !== undefined);
  return {
    data:
      follows.data && regionsReady && groupsReady
        ? joinTrackedPages(
            follows.data,
            directory.data && operators.module
              ? mergeOperatorGroups(directory.data, operators.module.OPERATOR_GROUPS)
              : [],
            { get: (slug) => regions.data?.get(slug) },
          )
        : undefined,
    isLoading:
      follows.isLoading ||
      (needsRegions && regions.isLoading) ||
      (needsGroups && !operators.module && !operators.error) ||
      (needsGroups && directory.isPending && !directory.error),
    error:
      follows.error ??
      (needsRegions ? regions.error : null) ??
      (needsGroups && operators.error ? { message: operators.error.message } : null) ??
      (needsGroups ? directory.error : null),
  };
};

// --- Recent updates rows (H4, D-2) ----------------------------------------------------------------

export const ACTIVITY_LABELS: Record<Activity.Type, string> = {
  'analyst-note-added': 'Analyst note added',
  'model-updated': 'Financial model updated',
  'regulatory-update': 'Regulatory update',
  'share-refreshed': 'Market share refreshed',
  'news-published': 'News published',
  'follow-added': 'Page followed',
};

const FIXTURE_EVENT_TYPE: Record<PlatformUpdate['kind'], Activity.Type> = {
  'analyst-note': 'analyst-note-added',
  'financial-model': 'model-updated',
  regulatory: 'regulatory-update',
  'market-share': 'share-refreshed',
};

/** @desc Figma's entity cell: "Operator information: Ladbrokes". */
export const entityLabel = (entity: { type: SubjectRef['kind']; name: string }): string =>
  `${entity.type === 'operator' ? 'Operator' : 'Region'} information: ${entity.name}`;

/** @desc One row of the Recent updates table. */
export type UpdateRow = {
  id: string;
  typeLabel: string;
  entityLabel: string;
  /** @desc A second line under the entity: the note's title and author, or a live event's title. */
  detail?: string;
  occurredAt: Date;
  subject: SubjectRef;
  /** @desc The analyst note leads the table, highlighted (D-2). */
  highlighted?: boolean;
  /** @desc Seeded sample content or a fixture event: badge it. */
  illustrative?: boolean;
};

/** @desc A fixture event has a date and no time; it is placed at 09:00 UTC so the time cell is honest about its grain. */
const fixtureEvent = (update: PlatformUpdate): Activity.Event => ({
  id: update.id,
  type: FIXTURE_EVENT_TYPE[update.kind],
  title: update.title,
  entity: { type: update.subject.kind, slug: update.subject.slug, name: update.subject.name },
  occurredAt: new Date(`${update.date}T09:00:00Z`),
});

/**
 * @desc One activity event as an update row. A live event's own title (the story's headline, "Followed
 * Entain") is its second line; a fixture event's title only restates its type and entity, so it has none.
 */
export const eventRow = (event: Activity.Event, illustrative: boolean): UpdateRow => ({
  id: event.id,
  typeLabel: ACTIVITY_LABELS[event.type],
  entityLabel: entityLabel(event.entity),
  ...(illustrative ? {} : { detail: event.title }),
  occurredAt: event.occurredAt,
  subject: { kind: event.entity.type, slug: event.entity.slug, name: event.entity.name },
  ...(illustrative ? { illustrative } : {}),
});

const noteRow = (note: Content.AnalystNote): UpdateRow => ({
  id: `note:${note.id}`,
  typeLabel: ACTIVITY_LABELS['analyst-note-added'],
  entityLabel: entityLabel(note.subject),
  detail: `${note.title} — ${note.author.name}`,
  occurredAt: note.publishedAt,
  subject: { kind: note.subject.type, slug: note.subject.slug, name: note.subject.name },
  highlighted: true,
  ...(note.illustrative ? { illustrative: true } : {}),
});

export type UpdateRows = {
  rows: UpdateRow[];
  /** @desc The event rows are the fixture's, because the `activity` feed is still empty. */
  fixtureEvents: boolean;
};

/**
 * @desc The Recent updates rows (H4): the analyst note first and highlighted (D-2), then the live
 * events; while the feed is empty, the badged fixture events instead — minus the fixture's own
 * note row when a real note is present, so the table never announces two notes.
 */
export const updateRows = (
  note: Content.AnalystNote | null | undefined,
  events: readonly Activity.Event[],
  fixture: readonly PlatformUpdate[] = HOME.recentUpdates,
  limit = 4,
): UpdateRows => {
  const fixtureEvents = events.length === 0;
  const source = fixtureEvents
    ? fixture.filter((update) => !(note && update.kind === 'analyst-note')).map(fixtureEvent)
    : events;
  const rows = [
    ...(note ? [noteRow(note)] : []),
    ...source.map((event) => eventRow(event, fixtureEvents)),
  ];
  return { rows: rows.slice(0, limit), fixtureEvents };
};
